import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, TrendingDown, DollarSign, Target, AlertCircle } from 'lucide-react';
import { FieldData, NDVIData, YieldPrediction as YieldPredictionType } from './types';
import { satelliteApi } from './satelliteApi';
import { cn } from '@/lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface YieldPredictionProps {
  field: FieldData;
  ndviData: NDVIData[];
}

export const YieldPrediction: React.FC<YieldPredictionProps> = ({ field, ndviData }) => {
  const { t } = useTranslation();
  const [prediction, setPrediction] = useState<YieldPredictionType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ndviData.length > 0) {
      calculatePrediction();
    }
  }, [field.id, ndviData]);

  const calculatePrediction = async () => {
    setLoading(true);
    try {
      const data = await satelliteApi.calculateYieldPrediction(field, ndviData);
      setPrediction(data);
    } catch (error) {
      console.error('Failed to calculate yield prediction:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!prediction) {
    return null;
  }

  const totalExpectedYield = prediction.expected * field.area;
  const totalRevenue = totalExpectedYield * (prediction.marketPrice || 0);
  const vsHistorical = prediction.historicalAverage
    ? ((prediction.expected - prediction.historicalAverage) / prediction.historicalAverage) * 100
    : 0;

  // Prepare data for factors chart
  const factorsData = prediction.factors.map((factor) => ({
    name: factor.name,
    impact: factor.impact,
    description: factor.description,
  }));

  return (
    <div className="space-y-4">
      {/* Main Prediction */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Yield Prediction
          </CardTitle>
          <CardDescription>AI-powered forecast based on satellite data</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Expected Yield */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
              <div className="text-sm text-muted-foreground mb-1">Expected Yield</div>
              <div className="text-3xl font-bold text-foreground">
                {prediction.expected.toLocaleString()} kg/ha
              </div>
              <div className="text-sm text-muted-foreground mt-2">
                Total: {totalExpectedYield.toLocaleString()} kg ({field.area} ha)
              </div>
            </div>

            <div className="p-4 rounded-lg bg-muted/50">
              <div className="text-sm text-muted-foreground mb-1">Estimated Revenue</div>
              <div className="text-3xl font-bold text-foreground flex items-center gap-1">
                <DollarSign className="h-6 w-6" />
                {totalRevenue.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground mt-2">
                @ ${prediction.marketPrice}/kg market price
              </div>
            </div>
          </div>

          {/* Confidence Range */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Confidence Range</span>
              <Badge variant="secondary">{prediction.confidence}% confidence</Badge>
            </div>
            <div className="relative">
              <Progress value={prediction.confidence} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{prediction.confidenceMin.toLocaleString()} kg/ha</span>
                <span>{prediction.confidenceMax.toLocaleString()} kg/ha</span>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-600">
                Actual yield will likely fall between{' '}
                <strong>
                  {prediction.confidenceMin.toLocaleString()} - {prediction.confidenceMax.toLocaleString()} kg/ha
                </strong>{' '}
                based on current conditions and historical data.
              </p>
            </div>
          </div>

          {/* Historical Comparison */}
          {prediction.historicalAverage && (
            <div className="p-4 rounded-lg border bg-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-muted-foreground">vs. Historical Average</div>
                  <div className="text-xl font-bold text-foreground mt-1">
                    {prediction.historicalAverage.toLocaleString()} kg/ha
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2">
                    {vsHistorical > 0 ? (
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    ) : (
                      <TrendingDown className="h-5 w-5 text-red-600" />
                    )}
                    <span
                      className={cn(
                        'text-xl font-bold',
                        vsHistorical > 0 ? 'text-green-600' : 'text-red-600'
                      )}
                    >
                      {vsHistorical > 0 ? '+' : ''}
                      {vsHistorical.toFixed(1)}%
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">difference</div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Factors Affecting Yield */}
      <Card>
        <CardHeader>
          <CardTitle>{t('features.satellite.factorsAffectingYield')}</CardTitle>
          <CardDescription>{t('features.satellite.impactAnalysis')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={factorsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" domain={[-30, 30]} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={12} width={120} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [`${value > 0 ? '+' : ''}${value}%`, 'Impact']}
                />
                <Bar dataKey="impact" radius={4}>
                  {factorsData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.impact > 0 ? 'hsl(var(--primary))' : 'hsl(var(--destructive))'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            <div className="space-y-2">
              {prediction.factors.map((factor, index) => (
                <div key={index} className="p-3 rounded-lg border bg-card">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm text-foreground">{factor.name}</span>
                    <Badge variant={factor.impact > 0 ? 'default' : 'destructive'}>
                      {factor.impact > 0 ? '+' : ''}
                      {factor.impact}%
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{factor.description}</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
